import type { PageItem, PageLocaleVariantListing } from '@/db/content';
import type { OptimisedImage } from '@/types/images';
import { isOptimisedImage } from '@/types/images';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocaleFromPath,
  stripLocalePrefix,
} from '@/i18n';

/** Replace Wagtail pages detail URL segment `/pages/<id>/` with another id. */
export function replaceDetailUrlPageId(detailUrl: string, newId: number): string {
  return detailUrl.replace(/\/pages\/(\d+)\//, `/pages/${newId}/`);
}

/**
 * Canonical listing rows plus synthetic items from `locale_variants` (same shape as full multi-tree listing).
 */
export function expandListingItemsWithLocaleVariants(items: PageItem[]): PageItem[] {
  const out: PageItem[] = [];
  const seen = new Set<number>();
  for (const item of items) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      out.push(item);
    }
  }
  for (const item of items) {
    const variants = item.locale_variants;
    if (!variants) continue;
    for (const [locale, v] of Object.entries(variants) as [
      string,
      PageLocaleVariantListing | undefined,
    ][]) {
      if (!v || seen.has(v.id)) continue;
      seen.add(v.id);
      out.push({
        id: v.id,
        title: v.title,
        meta: {
          type: item.meta.type,
          detail_url: replaceDetailUrlPageId(item.meta.detail_url, v.id),
          html_url: item.meta.html_url ?? null,
          url_path: v.url_path,
          path: v.path,
          slug: v.slug,
          first_published_at: item.meta.first_published_at ?? null,
          last_published_at: v.last_published_at,
          parent: item.meta.parent ?? null,
          locale,
          translation_key: item.meta.translation_key,
          show_in_menus: item.meta.show_in_menus,
          seo_title: item.meta.seo_title ?? null,
          search_description: item.meta.search_description ?? null,
          icon: item.meta.icon ?? null,
        },
      });
    }
  }
  return out.sort((a, b) => a.meta.path.localeCompare(b.meta.path));
}

/** Map translation_key → locale → page row (expanded listing; used for nav and sidebar). */
export function indexPageItemsByTranslationKey(
  items: PageItem[]
): Map<string, Map<string, PageItem>> {
  const itemsByTranslationKey = new Map<string, Map<string, PageItem>>();
  for (const item of items) {
    const key = item.meta.translation_key;
    if (!key) continue;
    if (!itemsByTranslationKey.has(key)) {
      itemsByTranslationKey.set(key, new Map());
    }
    itemsByTranslationKey.get(key)!.set(item.meta.locale, item);
  }
  return itemsByTranslationKey;
}

/** Legacy listing/cache payload: single raster URL. New API: same shape as other optimised Wagtail images. */
export interface SiteLogoLegacy {
  id: number;
  title: string;
  url: string;
  width: number;
  height: number;
}

export type SiteLogo = (OptimisedImage & { id: number }) | SiteLogoLegacy;

export function isOptimisedSiteLogo(logo: SiteLogo): logo is OptimisedImage & { id: number } {
  return isOptimisedImage(logo);
}

export interface SocialLink {
  label: string;
  url: string;
  icon?: string;
}

/** A page node in the nav tree with an ordered list of children. */
export interface NavPage {
  title: string;
  url: string;
  locale: string;
  translation_key?: string;
  children: NavPage[];
}

/** Top nav data: site root reference plus top-level nav items. */
export interface TopNavData {
  /** Optional site root title; lean payloads may omit this and let the store resolve it. */
  siteRootTitle?: string;
  /** Optional site root URL; lean payloads may omit this and let the store resolve it. */
  siteRootUrl?: string;
  /** Optional translation key of the site root; lean payloads may omit this. */
  siteRootTranslationKey?: string;
  /** Page id of the site root for the current locale (for looking up detail e.g. site_name). */
  siteRootId?: number;
  /** Site name from root page detail (set by content store); defaults to 'INDMO'. */
  siteName?: string;
  /** Site subtitle from root page detail (HomePage.site_subtitle). */
  siteSubtitle?: string;
  /** Site logos from root page detail (HomePage.site_logos). */
  siteLogos?: SiteLogo[];
  /** Single footer brand logo from HomePage.footer_logo. */
  footerLogo?: SiteLogo | null;
  /** Text beside the footer logo (HomePage.footer_logo_text). */
  footerLogoText?: string;
  /** Social links from root page detail (HomePage.social_links). */
  socialLinks?: SocialLink[];
  /** Copyright text from root page detail. */
  copyrightText?: string;
  /** Top menu links from root page detail (HomePage.top_menu_links). */
  topMenu?: {
    pages: {
      id?: number;
      page_id?: number;
      title?: string;
      url?: string;
      translation_key?: string;
      icon?: string;
    }[];
  };
  /**
   * Footer columns from HomePage.footer_link_columns (editable title + page chooser links).
   * URLs and titles are resolved in the content store using the page listing.
   */
  footerLinkColumns?: Array<{
    title: string;
    links: { page_id: number }[];
  }>;
  /** Legal row in the site footer: ordered internal page ids (HomePage). */
  footerLegalLinks?: { page_id: number }[];
  /** Footer contact line (HomePage); empty means hidden. */
  footerContactAddress?: string;
  footerContactPhone?: string;
  footerContactEmail?: string;
  /** Top-level nav items (children of the site root). */
  rootPages: NavPage[];
}

/** One footer column after resolving page ids to locale-aware title + URL. */
export type ResolvedFooterLinkColumn = {
  title: string;
  items: { title: string; url: string }[];
};

/** Normalize path to a trailing-slash form. */
function normalizePath(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return p.endsWith('/') ? p : p + '/';
}

/** Get the path for a page: prefer url_path, else derive from html_url. */
function getPathForItem(item: PageItem): string {
  if (item.meta.url_path) {
    return normalizePath(item.meta.url_path);
  }
  if (item.meta.html_url) {
    try {
      const p = new URL(item.meta.html_url, 'http://x').pathname;
      return p.endsWith('/') ? p : p + '/';
    } catch {
      return '/';
    }
  }
  return '/';
}

/**
 * Compute per-locale root path (shortest url_path by depth for that locale)
 * and the default locale (locale with shallowest root, matching sitemap behaviour).
 */
export function getRootPathsAndDefaultLocale(items: PageItem[]): {
  rootPathByLocale: Map<string, string>;
  defaultLocale: string;
} {
  const byLocale = new Map<string, string[]>();
  for (const item of items) {
    const path = getPathForItem(item);
    const locale = item.meta.locale;
    if (!byLocale.has(locale)) byLocale.set(locale, []);
    byLocale.get(locale)!.push(path);
  }
  const rootPathByLocale = new Map<string, string>();
  let defaultLocale = '';
  let minRootDepth = Infinity;
  for (const [locale, paths] of byLocale.entries()) {
    const rootPath = paths.reduce((a, b) => (pathDepth(a) <= pathDepth(b) ? a : b));
    rootPathByLocale.set(locale, rootPath);
    const d = pathDepth(rootPath);
    if (d < minRootDepth) {
      minRootDepth = d;
      defaultLocale = locale;
    }
  }
  return { rootPathByLocale, defaultLocale };
}

/**
 * Build URL for a page: same logic as sitemap. Default locale has no prefix;
 * other locales use /{locale}/{path_part} where path_part is url_path relative to locale root.
 */
export function canonicalizeUrlPathForLocale(
  urlPath: string,
  locale: string,
  rootPathByLocale: Map<string, string>,
  defaultLocale: string
): string {
  const normalizedUrlPath = normalizePath(urlPath);
  const rootPath = rootPathByLocale.get(locale) ?? '/';
  const pathPart = rootPath && normalizedUrlPath.startsWith(rootPath)
    ? normalizedUrlPath.slice(rootPath.length).replace(/^\/+/, '')
    : normalizedUrlPath.replace(/^\/+/, '');
  const suffix = pathPart && !pathPart.endsWith('/') ? `${pathPart}/` : pathPart;
  const isDefaultLocale = locale === defaultLocale;
  if (isDefaultLocale) {
    return suffix ? `/${suffix}` : '/';
  }
  return suffix ? `/${locale}/${suffix}` : `/${locale}/`;
}

export function getUrlForItem(
  item: PageItem,
  rootPathByLocale: Map<string, string>,
  defaultLocale: string
): string {
  const rawPath = item.meta.url_path ? normalizePath(item.meta.url_path) : getPathForItem(item);
  return canonicalizeUrlPathForLocale(rawPath, item.meta.locale, rootPathByLocale, defaultLocale);
}

/**
 * Build the public URL for `targetLocale` when only the default-locale URL is known
 * (e.g. Wagtail alias pages are hidden from the listing — no per-locale row until translated).
 */
export function localePrefixedPublicUrlFromDefault(
  defaultLocalePublicUrl: string,
  targetLocale: string,
  defaultLocale: string
): string {
  if (targetLocale === defaultLocale) return defaultLocalePublicUrl;
  const n = normalizePathname(urlToPathname(defaultLocalePublicUrl));
  if (n === '/' || n === '') return normalizePathname(`/${targetLocale}/`);
  const inner = n.slice(1, -1);
  if (!inner) return normalizePathname(`/${targetLocale}/`);
  return normalizePathname(`/${targetLocale}/${inner}/`);
}

function pathDepth(path: string): number {
  return path.split('/').filter(Boolean).length;
}

/** Section length for Wagtail/MP_Node materialized path (e.g. 0001, 0002). */
const PATH_SECTION_LENGTH = 4;

/**
 * Parent path of a node: same path with the last 4-character section removed.
 * Returns null for root-level paths (length <= 4).
 */
function parentMaterializedPath(path: string): string | null {
  if (path.length <= PATH_SECTION_LENGTH) return null;
  return path.slice(0, -PATH_SECTION_LENGTH);
}

export function buildNavTree(items: PageItem[]): TopNavData {
  return buildNavTreeForLocale(items, null);
}

/**
 * Build nav tree for a single locale. Pass locale code (e.g. 'tet') to show only that locale's
 * page tree; pass null to use all items (original behaviour).
 * NOTE: Sidebar nav is no longer built from this; it comes from GET /api/v2/site_navigation/.
 * This is still used for translation links and any code that needs a tree from page listing.
 */
export function buildNavTreeForLocale(
  items: PageItem[],
  locale: string | null
): TopNavData {
  const filtered =
    locale != null ? items.filter((item) => item.meta.locale === locale) : items;
  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(filtered);
  type PathInfo = {
    id: number;
    title: string;
    url: string;
    locale: string;
    translation_key?: string;
    show_in_menus: boolean;
  };
  const pathInfo = new Map<string, PathInfo>();
  for (const item of filtered) {
    const path = item.meta.path;
    if (!path) continue;
    pathInfo.set(path, {
      id: item.id,
      title: item.title,
      url: getUrlForItem(item, rootPathByLocale, defaultLocale),
      locale: item.meta.locale,
      translation_key: item.meta.translation_key,
      show_in_menus: item.meta.show_in_menus !== false,
    });
  }
  const sortedPaths = [...pathInfo.keys()].sort();
  const pathToNode = new Map<string, NavPage>();
  const roots: NavPage[] = [];
  let firstRootPath: string | null = null;
  for (const path of sortedPaths) {
    const { title, url, locale: nodeLocale, translation_key, show_in_menus } =
      pathInfo.get(path)!;
    const node: NavPage = { title, url, locale: nodeLocale, translation_key, children: [] };
    pathToNode.set(path, node);
    const parentPathKey = parentMaterializedPath(path);
    const parentNode = parentPathKey != null ? pathToNode.get(parentPathKey) : undefined;
    if (parentNode) {
      if (show_in_menus) parentNode.children.push(node);
    } else {
      // No parent => root of the tree; always include so we have a site root
      if (roots.length === 0) firstRootPath = path;
      roots.push(node);
    }
  }
  // Single root: first link = root title/url; top-level nav = its children. Multiple roots: first link = "/", top-level = roots.
  if (roots.length === 1) {
    const root = roots[0]!;
    const siteRootId = firstRootPath != null ? pathInfo.get(firstRootPath)?.id : undefined;
    return {
      siteRootTitle: root.title,
      siteRootUrl: root.url,
      siteRootTranslationKey: root.translation_key,
      siteRootId,
      rootPages: root.children,
    };
  }
  return {
    siteRootTitle: roots[0]?.title ?? '',
    siteRootUrl: '/',
    rootPages: roots,
  };
}

/**
 * Localize a nav tree: keep structure (from Tetum) but show title and URL for the given
 * display locale when a translation exists, otherwise fall back to Tetum.
 */
function localizeNavTreeRec(
  node: NavPage,
  byKey: Map<string, Map<string, PageItem>>,
  rootPathByLocale: Map<string, string>,
  defaultLocale: string,
  displayLocale: string
): NavPage {
  let title = node.title;
  let url = node.url;
  if (node.translation_key) {
    const translated = byKey.get(node.translation_key)?.get(displayLocale);
    if (translated) {
      title = translated.title;
      url = getUrlForItem(translated, rootPathByLocale, defaultLocale);
    } else if (displayLocale !== defaultLocale) {
      url = localePrefixedPublicUrlFromDefault(node.url, displayLocale, defaultLocale);
    }
  }
  const children = node.children.map((child) =>
    localizeNavTreeRec(child, byKey, rootPathByLocale, defaultLocale, displayLocale)
  );
  return { ...node, title, url, children };
}

/**
 * Return nav tree with titles and URLs localized to the given locale.
 * Structure (which pages exist) comes from the Tetum tree; links and labels use the
 * active language when a translation exists, otherwise Tetum.
 */
export function localizeNavTree(
  tetumNav: TopNavData,
  allItems: PageItem[],
  displayLocale: string
): TopNavData {
  if (displayLocale === DEFAULT_LOCALE) return tetumNav;
  const byKey = indexPageItemsByTranslationKey(allItems);
  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(allItems);
  let siteRootTitle = tetumNav.siteRootTitle;
  let siteRootUrl = tetumNav.siteRootUrl;
  let siteRootId = tetumNav.siteRootId;
  if (tetumNav.siteRootTranslationKey) {
    const rootItem = byKey.get(tetumNav.siteRootTranslationKey)?.get(displayLocale);
    if (rootItem) {
      siteRootTitle = rootItem.title;
      siteRootUrl = getUrlForItem(rootItem, rootPathByLocale, defaultLocale);
      siteRootId = rootItem.id;
    } else if (tetumNav.siteRootUrl != null) {
      siteRootUrl = localePrefixedPublicUrlFromDefault(
        tetumNav.siteRootUrl,
        displayLocale,
        defaultLocale
      );
    }
  }
  const rootPages = (tetumNav.rootPages ?? []).map((node) =>
    localizeNavTreeRec(node, byKey, rootPathByLocale, defaultLocale, displayLocale)
  );
  return {
    ...tetumNav,
    siteRootTitle,
    siteRootUrl,
    siteRootId,
    rootPages,
  };
}

/** Single translation link for the language switcher. */
export interface TranslationLink {
  locale: string;
  url: string;
  title: string;
}

/**
 * Return links to the same page in other languages (by translation_key).
 * Used for the language switcher. Returns empty array if pathname does not resolve to a page
 * or the page has no translation_key.
 */
export function getTranslationLinks(
  items: PageItem[],
  pathname: string
): TranslationLink[] {
  const page = resolvePageFromPathname(items, pathname);
  if (!page?.meta.translation_key) return [];
  const key = page.meta.translation_key;
  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
  const byKey = indexPageItemsByTranslationKey(items);
  const defaultPublic = getUrlForItem(page, rootPathByLocale, defaultLocale);
  const links: TranslationLink[] = [];
  for (const loc of SUPPORTED_LOCALES) {
    const row = byKey.get(key)?.get(loc);
    if (row) {
      links.push({
        locale: loc,
        url: getUrlForItem(row, rootPathByLocale, defaultLocale),
        title: row.title,
      });
    } else {
      links.push({
        locale: loc,
        url: localePrefixedPublicUrlFromDefault(defaultPublic, loc, defaultLocale),
        title: page.title,
      });
    }
  }
  return links;
}

/** Decode pathname if it contains percent-encoding (e.g. from history). */
function tryDecodePathname(pathname: string): string {
  try {
    return decodeURI(pathname);
  } catch {
    return pathname;
  }
}

/** Normalize pathname (e.g. from route.path) to match built URLs (leading + trailing slash). */
function normalizePathname(pathname: string): string {
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (p === '/' || p === '') return '/';
  return p.endsWith('/') ? p : `${p}/`;
}

/** Pathname part of a built URL (handles full html_url or path-only). */
function urlToPathname(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }
  return url;
}

/**
 * Resolve the current pathname to a page. Uses the same URL rules as the nav tree.
 * Returns the matching PageItem or null if none.
 */
export function resolvePageFromPathname(
  items: PageItem[],
  pathname: string
): PageItem | null {
  if (!items.length) return null;
  const decoded = tryDecodePathname(pathname);
  const normalized = normalizePathname(decoded);
  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
  for (const item of items) {
    const url = getUrlForItem(item, rootPathByLocale, defaultLocale);
    const itemPathname = normalizePathname(urlToPathname(url));
    if (itemPathname === normalized) return item;
  }
  const withSlash = decoded.startsWith('/') ? decoded : `/${decoded}`;
  const pathLocale = getLocaleFromPath(withSlash);
  if (pathLocale === defaultLocale) return null;
  const stripped = stripLocalePrefix(withSlash);
  const inner = normalizePathname(
    stripped === '' || stripped === '/' ? '/' : stripped.startsWith('/') ? stripped : `/${stripped}`
  );
  for (const item of items) {
    if (item.meta.locale !== defaultLocale) continue;
    const url = getUrlForItem(item, rootPathByLocale, defaultLocale);
    const itemPathname = normalizePathname(urlToPathname(url));
    if (itemPathname === inner) return item;
  }
  return null;
}
